/**
 * Client-side image compression using Canvas API.
 * TIF/TIFF files are processed server-side via sharp for proper ICC profile handling.
 * Regular images (JPEG, PNG, WebP) are compressed client-side.
 */

export interface CompressOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
}

const DEFAULTS: Required<CompressOptions> = {
  maxWidth: 1400,
  maxHeight: 1400,
  quality: 0.78,
};

/** Format bytes into human-readable string */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}

/** Check if the file needs server-side processing (TIF, TIFF, or very large files) */
function needsServerProcessing(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.tif') || name.endsWith('.tiff') || file.size > 15 * 1024 * 1024;
}

/** Crop area type from react-easy-crop */
export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Crop an image given a data URL and a crop area.
 * Returns a new JPEG base64 data URL.
 */
export function getCroppedImage(
  imageSrc: string,
  cropArea: CropArea,
  quality = 0.85,
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = cropArea.width;
      canvas.height = cropArea.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(
        img,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        cropArea.width,
        cropArea.height,
      );
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = () => reject(new Error('Failed to load image for cropping'));
    img.src = imageSrc;
  });
}

/**
 * Process image server-side via sharp (handles TIF, ICC profiles, large files).
 */
async function processOnServer(
  file: File,
  maxWidth: number,
  maxHeight: number,
  quality: number,
): Promise<{ dataUrl: string; originalSize: number; compressedSize: number }> {
  const formData = new FormData();
  formData.append('image', file);
  formData.append('maxWidth', String(maxWidth));
  formData.append('maxHeight', String(maxHeight));
  formData.append('quality', String(Math.round(quality * 100)));

  const adminKey = localStorage.getItem('gallery-admin-key') || '';
  const res = await fetch('/api/v1/admin/process-image', {
    method: 'POST',
    headers: { 'X-Admin-Key': adminKey },
    body: formData,
  });

  if (!res.ok) {
    throw new Error(`Server image processing failed: ${res.status}`);
  }

  const json = await res.json();
  if (!json.success) throw new Error(json.error || 'Image processing failed');

  return {
    dataUrl: json.data.dataUrl,
    originalSize: json.data.originalSize,
    compressedSize: json.data.compressedSize,
  };
}

/**
 * Compress an image file.
 * - TIF/TIFF and large files (>15MB) → server-side processing via sharp
 * - Regular images → client-side Canvas compression
 */
export async function compressImage(
  file: File,
  options?: CompressOptions,
): Promise<{ dataUrl: string; originalSize: number; compressedSize: number }> {
  const { maxWidth, maxHeight, quality } = { ...DEFAULTS, ...options };

  // TIF/TIFF or very large files: process server-side with sharp
  if (needsServerProcessing(file)) {
    return processOnServer(file, maxWidth, maxHeight, quality);
  }

  // Regular images: client-side Canvas compression
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();

    img.onload = () => {
      URL.revokeObjectURL(url);

      let w = img.naturalWidth;
      let h = img.naturalHeight;
      if (w > maxWidth || h > maxHeight) {
        const ratio = Math.min(maxWidth / w, maxHeight / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }

      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);

      const dataUrl = canvas.toDataURL('image/jpeg', quality);
      const compressedSize = Math.round((dataUrl.length * 3) / 4);
      resolve({ dataUrl, originalSize: file.size, compressedSize });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Failed to load image: ${file.name}`));
    };

    img.src = url;
  });
}
